// BriefEditor — TipTap-backed editor for brief drafts.
//
// Custom blocks: footnoteRef, endnoteRef (Insert Reference menu), plus footer
// blocks (author bio / product CTA / affiliate CTA). Bibliography is inserted
// via a multi-select picker that persists references on the piece.

import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Link from "@tiptap/extension-link";
import Paragraph from "@tiptap/extension-paragraph";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import { WordPasteSanitizer } from "@/lib/editor/wordPasteSanitizer";
import { BlockIndent } from "@/lib/editor/indentExtension";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const ClassParagraph = Paragraph.extend({
  addAttributes() {
    return {
      class: {
        default: null,
        parseHTML: (el) => el.getAttribute("class"),
        renderHTML: (attrs) => (attrs.class ? { class: attrs.class as string } : {}),
      },
    };
  },
});

import { SignatureImageNode } from "./nodes/SignatureImageNode";
import { FootnoteRefNode } from "./nodes/FootnoteRefNode";
import { EndnoteRefNode } from "./nodes/EndnoteRefNode";
import { LegacyImageCaptionNode } from "./nodes/LegacyImageCaptionNode";
import { RefMarkerDecoration } from "./nodes/RefMarkerDecoration";
import { sanitizeEditorContent } from "./sanitizeEditorContent";
import { BriefEditorContext, type BriefEditorContextValue } from "./briefEditorContext";
import { BriefEditorToolbar, type ApprovedBriefToolbarControl } from "./BriefEditorToolbar";
import { BibliographyPickerDialog } from "./BibliographyPickerDialog";
import { InsertCitationDialog, type InsertedCitation } from "./InsertCitationDialog";


import { composerBus } from "@/lib/composerBus";
import { supabase } from "@/integrations/supabase/client";
import {
  buildLibraryQuoteInsert,
  type LibraryQuoteSource,
} from "@/lib/composer/libraryQuoteInsert";
import { LibraryDropConfirmPopover } from "./LibraryDropConfirmPopover";
import { buildBriefEditorDropHandler } from "./briefEditorDropHandler";
import { buildExcerptCitationHtml } from "@/lib/composer/excerptDropFormat";
import { useWorkspace } from "@/hooks/useWorkspace";
import { attachSourceToPiece } from "@/lib/composer/attachPieceSource";
import { toast } from "sonner";
import { tierEntitlements } from "@/lib/tier";
import { notifyHostDropHandled, suppressHostFileDrag } from "@/lib/composerHostDropReset";

export type BriefEditorProps = {
  value: unknown;
  onChange: (json: unknown) => void;
  context: BriefEditorContextValue;
  placeholder?: string;
  editable?: boolean;
  workspaceId?: string | null;
  /**
   * When provided, the editor's content is replaced with this TipTap doc
   * (sanitized) without emitting an onChange. Used by the AI Chat
   * "Write to canvas" mode to stream cumulative buffers into a locked
   * editor without thrashing the parent's autosave.
   */
  streamContent?: unknown;
  /** Optional Approved-Brief control forwarded to the toolbar. */
  approvedBriefControl?: ApprovedBriefToolbarControl;
  /** Hide editing controls for read-only viewer surfaces. */
  showToolbar?: boolean;
};


const newId = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `fn-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
};

export const BriefEditor = ({
  value,
  onChange,
  context,
  placeholder = "Start writing your brief…",
  editable = true,
  workspaceId = null,
  streamContent,
  approvedBriefControl,
  showToolbar = true,
}: BriefEditorProps) => {

  const [associatesTag, setAssociatesTag] = useState<string | null>(null);
  const associatesTagRef = useRef<string | null>(null);
  const pieceIdRef = useRef<string | null>(context.briefId);
  useEffect(() => { pieceIdRef.current = context.briefId; }, [context.briefId]);
  useEffect(() => { associatesTagRef.current = associatesTag; }, [associatesTag]);
  const ctxRef = useRef(context);
  useEffect(() => { ctxRef.current = context; }, [context]);
  const handleSourceInserted = useCallback((libraryItemId: string, label: string) => {
    const ctx = ctxRef.current;
    if (!ctx.workspaceId || !ctx.briefId) return;
    void (async () => {
      try {
        await attachSourceToPiece(ctx.workspaceId!, ctx.briefId!, libraryItemId, label);
        await ctx.flushPendingSave?.();
        await ctx.notifyChanged?.();
      } catch (err) {
        toast.error((err as Error).message || "Could not add this source to the piece's citations");
      }
    })();
  }, []);
  const refreshCitations = useCallback(() => {
    void ctxRef.current.notifyChanged?.();
  }, []);
  useEffect(() => {
    if (!workspaceId) {
      setAssociatesTag(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("amazon_associates_tag")
        .eq("id", workspaceId)
        .maybeSingle<{ amazon_associates_tag: string | null }>();
      if (!cancelled) setAssociatesTag(data?.amazon_associates_tag ?? null);
    })().catch(() => { if (!cancelled) setAssociatesTag(null); });
    return () => { cancelled = true; };
  }, [workspaceId]);

  const pendingLibDropRef = useRef<{ source: LibraryQuoteSource; pos: number; libraryItemId: string | null } | null>(null);
  const pendingExcerptDropRef = useRef<import("./briefEditorDropHandler").ExcerptDropPending | null>(null);
  const [libDropOpen, setLibDropOpen] = useState(false);
  const [libDropAnchor, setLibDropAnchor] = useState<{ x: number; y: number } | null>(null);
  const [libDropTitle, setLibDropTitle] = useState<string>("");
  const [bibPickerOpen, setBibPickerOpen] = useState(false);
  const [citeDialogOpen, setCiteDialogOpen] = useState(false);

  const extensions = useMemo(
    () => [
      StarterKit.configure({ codeBlock: false, paragraph: false, horizontalRule: { HTMLAttributes: { class: "my-4" } } }),
      ClassParagraph,
      Placeholder.configure({ placeholder }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer" } }),
      Underline,
      TextAlign.configure({ types: ["paragraph", "heading"] }),
      BlockIndent,
      WordPasteSanitizer,
      SignatureImageNode,
      FootnoteRefNode,
      EndnoteRefNode,
      RefMarkerDecoration.configure({ getPieceId: () => pieceIdRef.current }),
      // Compatibility shim — keep registered even though the insert UI is gone.
      LegacyImageCaptionNode,
    ],
    [placeholder],
  );

  // Pre-sanitize stored content so unknown legacy node types degrade to
  // empty paragraphs instead of throwing "Unknown node type" during editor
  // init (the recurring reload-page stutter).
  const initialContent = useMemo(
    () => sanitizeEditorContent(value ?? { type: "doc", content: [{ type: "paragraph" }] }),
    // Only sanitize on mount; subsequent edits go through onUpdate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const editorRef = useRef<import("@tiptap/react").Editor | null>(null);

  const { workspaces } = useWorkspace();
  const workspaceMediaCapBytes = useMemo(() => {
    if (!workspaceId) return null;
    const ws = workspaces.find((w) => w.id === workspaceId);
    return tierEntitlements(ws?.account_tier ?? null).workspaceMediaBytes;
  }, [workspaceId, workspaces]);

  const baseDropHandler = useMemo(
    () => buildBriefEditorDropHandler({
      editorRef,
      associatesTagRef,
      pendingLibDropRef,
      pendingExcerptDropRef,
      setLibDropAnchor,
      setLibDropTitle,
      setLibDropOpen,
      onSourceInserted: handleSourceInserted,
    }),
    [handleSourceInserted],
  );

  const editor = useEditor({
    extensions,
    content: initialContent,
    editable,
    editorProps: {
      attributes: {
        class:
          "prose prose-sm sm:prose-base dark:prose-invert max-w-none h-full focus:outline-none px-4 py-3 prose-blockquote:not-italic prose-blockquote:font-normal [&_blockquote_p:first-of-type]:before:content-none [&_blockquote_p:last-of-type]:after:content-none",
      },
      handleDrop: (view, event, slice, moved) => {
        return baseDropHandler(view, event, slice, moved);
      },
    },
    onUpdate: ({ editor: ed }) => onChange(ed.getJSON()),
  });

  useEffect(() => { editorRef.current = editor; }, [editor]);

  const completeLibraryDrop = useCallback(
    (includeAffiliateLink: boolean) => {
      const pendingLib = pendingLibDropRef.current;
      const pendingExcerpt = pendingExcerptDropRef.current;
      pendingLibDropRef.current = null;
      pendingExcerptDropRef.current = null;
      setLibDropOpen(false);
      if (!editor) return;
      if (pendingLib) {
        const ins = buildLibraryQuoteInsert({
          source: pendingLib.source,
          workspaceAssociatesTag: associatesTag,
          includeAffiliateLink,
        });
        editor.chain().focus().insertContentAt(pendingLib.pos, ins.blockquoteNode).run();
        editor.chain().focus().insertContent(ins.attributionHtml).run();
        if (pendingLib.libraryItemId) {
          const s = pendingLib.source;
          handleSourceInserted(
            pendingLib.libraryItemId,
            [s.author, s.title].filter(Boolean).join(". ") + (s.published_year ? ` (${s.published_year})` : ""),
          );
        }
        return;
      }
      if (pendingExcerpt) {
        editor.chain().focus()
          .insertContentAt(
            pendingExcerpt.pos,
            `<blockquote><p>${pendingExcerpt.bodyHtml}</p></blockquote>`,
          )
          .run();
        const citeHtml = buildExcerptCitationHtml({
          ...pendingExcerpt.citationParts,
          affiliateHref: includeAffiliateLink ? pendingExcerpt.affiliateHref : null,
        });
        if (citeHtml) {
          editor.chain().focus().insertContent(citeHtml).run();
        }
        if (pendingExcerpt.libraryItemId) {
          handleSourceInserted(pendingExcerpt.libraryItemId, pendingExcerpt.label);
        }
      }
    },
    [editor, associatesTag, handleSourceInserted],
  );

  const cancelLibraryDrop = useCallback(() => {
    pendingLibDropRef.current = null;
    pendingExcerptDropRef.current = null;
    setLibDropOpen(false);
  }, []);

  useEffect(() => {
    if (editor && editor.isEditable !== editable) editor.setEditable(editable);
  }, [editor, editable]);

  // Stream mode: replace content from external buffer without emitting onChange.
  useEffect(() => {
    if (!editor || streamContent === undefined || streamContent === null) return;
    try {
      const sanitized = sanitizeEditorContent(streamContent);
      editor.commands.setContent(sanitized as never, false);
    } catch (err) {
      console.warn("BriefEditor: streamContent setContent failed", err);
    }
  }, [editor, streamContent]);


  useEffect(() => {
    if (!editor) return;
    return composerBus.subscribe("draft:insert", (payload) => {
      if (payload.mode !== "append-at-cursor") return;
      const raw = payload.doc as { type?: string; content?: unknown[] } | unknown;
      const content =
        raw && typeof raw === "object" && (raw as { type?: string }).type === "doc"
          ? ((raw as { content?: unknown[] }).content ?? [])
          : raw;
      editor.chain().focus().insertContent(content as never).run();
    });
  }, [editor]);

  const dragDepthRef = useRef(0);
  const [dragActive, setDragActive] = useState(false);
  const hasComposerPayload = (e: React.DragEvent) => {
    const types = Array.from(e.dataTransfer?.types ?? []);
    return (
      types.includes("application/x-excerptus-excerpt-ref") ||
      types.includes("application/x-excerptus-library-ref") ||
      types.includes("application/x-excerptus-block") ||
      types.includes("application/x-excerptus-blueprint") ||
      types.includes("application/x-excerptus-media")
    );
  };
  const hasFiles = (e: React.DragEvent) =>
    Array.from(e.dataTransfer?.types ?? []).includes("Files");
  const onDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (hasFiles(e)) suppressHostFileDrag(e);
    if (!hasComposerPayload(e)) return;
    dragDepthRef.current += 1;
    setDragActive(true);
  }, []);
  const onDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (hasFiles(e)) {
      suppressHostFileDrag(e);
      e.dataTransfer.dropEffect = "copy";
      return;
    }
    if (!hasComposerPayload(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }, []);
  const onDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (hasFiles(e)) suppressHostFileDrag(e);
    if (!hasComposerPayload(e)) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setDragActive(false);
  }, []);
  const onDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (hasFiles(e)) {
      suppressHostFileDrag(e);
      notifyHostDropHandled();
    }
    dragDepthRef.current = 0;
    setDragActive(false);
  }, []);

  if (!editor) return null;

  const insertFootnote = () =>
    editor.chain().focus().insertContent({
      type: "excerptus.footnoteRef",
      attrs: { id: newId(), body: "" },
    }).run();

  const insertEndnote = () =>
    editor.chain().focus().insertContent({
      type: "excerptus.endnoteRef",
      attrs: { id: newId(), body: "" },
    }).run();

  const insertBibliographyNode = () => {
    refreshCitations();
    editor.chain().focus().insertContent({
      type: "excerptus.bibliography",
      attrs: { hidden: false },
    }).run();
  };

  const openBibPicker = () => setBibPickerOpen(true);
  const openCiteDialog = () => setCiteDialogOpen(true);

  const handleCitationInserted = (result: InsertedCitation) => {
    if (!editor) return;
    refreshCitations();
    if (result.placement === "footnote") {
      // Body is intentionally empty — CitationsPreviewPanel renders the
      // short-cite from refId via the Chicago sequence (full → short →
      // Ibid). Leaving body empty also keeps the popover's body editor
      // free for optional author commentary appended after the citation.
      editor.chain().focus().insertContent({
        type: "excerptus.footnoteRef",
        attrs: {
          id: newId(),
          body: "",
          refId: result.refId,
          locator: result.locator,
        },
      }).run();
    } else {
      editor.chain().focus().insertContent(result.inlineText).run();
    }
  };

  return (
    <BriefEditorContext.Provider value={{ ...context, workspaceMediaCapBytes }}>
      <div
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={`flex h-full min-h-0 flex-col rounded-lg border bg-background transition-shadow ${
          dragActive ? "ring-2 ring-primary/60 ring-offset-2 ring-offset-background" : ""
        }`}
      >
        {showToolbar && editor ? (
          <BriefEditorToolbar
            editor={editor}
            onInsertFootnote={insertFootnote}
            onInsertEndnote={insertEndnote}
            onInsertBibliography={openBibPicker}
            onInsertCitation={openCiteDialog}
            approvedBrief={approvedBriefControl}
          />
        ) : null}
        <div className="min-h-0 flex-1 overflow-auto">
          <EditorContent editor={editor} className="h-full" />
        </div>
      </div>
      <LibraryDropConfirmPopover
        open={libDropOpen}
        onOpenChange={(open) => { if (!open) cancelLibraryDrop(); }}
        anchorPoint={libDropAnchor}
        itemTitle={libDropTitle}
        onConfirm={completeLibraryDrop}
        onCancel={cancelLibraryDrop}
      />
      <BibliographyPickerDialog
        open={bibPickerOpen}
        onOpenChange={setBibPickerOpen}
        workspaceId={workspaceId}
        pieceId={context.briefId}
        onInserted={insertBibliographyNode}
      />
      <InsertCitationDialog
        open={citeDialogOpen}
        onOpenChange={setCiteDialogOpen}
        workspaceId={workspaceId}
        pieceId={context.briefId}
        onInsert={handleCitationInserted}
      />
    </BriefEditorContext.Provider>
  );
};
